import { fetchWithRetry } from './retry';
import type {
    CIDResponse,
    CIDCheckResponse,
    EmailLinkStatusResponse,
} from '../types';

interface QuizEmailCheckRequest {
    cid: string;
    email: string;
}

interface QuizEmailCheckResponse {
    result: {
        cid: string;
    };
}

interface QuizEmailChangedRequest {
    cid: string;
    emailOld: string;
    emailNew: string;
    version: string;
}

interface OldFlowEmailChangedRequest extends QuizEmailChangedRequest {
    page_url: string;
}

interface QuizEmailChangedResponse {
    result: {
        cid: string;
    };
}

/**
 * Gets the initial CID
 */
export async function fetchInitialCID(
    apiEndpoint: string
): Promise<string> {
    return fetchWithRetry(
        async () => {
            const response = await fetch(`${apiEndpoint}/cid`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: CIDResponse = await response.json();

            if (!data.cid) {
                throw new Error('No CID in response');
            }

            return data.cid;
        },
        1,
        1000,
        'fetchInitialCID'
    );
}

/**
 * Updates/verifies CID from email (for regular flow)
 */
export async function updateCIDWithEmail(
    apiEndpoint: string,
    cid: string,
    email: string
): Promise<string> {
    return fetchWithRetry(
        async () => {
            const response = await fetch(`${apiEndpoint}/cid/check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cid, email }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: CIDCheckResponse = await response.json();

            if (!data.cid) {
                throw new Error('No CID in response');
            }

            return data.cid;
        },
        1,
        1000,
        'updateCIDWithEmail'
    );
}

/**
 * Email check for a quiz flow
 */
export async function getEmailCheck(
    apiEndpoint: string,
    flowEndpoint: string,
    cid: string,
    email: string
): Promise<string> {

    // TODO: need update iq-test flow path on server side
    const setFlowEndpoint = flowEndpoint === 'iq-test' ? 'iq' : flowEndpoint;

    return fetchWithRetry(
        async () => {
            const requestBody: QuizEmailCheckRequest = { cid, email };

            const response = await fetch(`${apiEndpoint}/quiz/${setFlowEndpoint}/email-check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                console.error(`[API] getEmailCheck error! status: ${response.status}`);
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: QuizEmailCheckResponse = await response.json();

            if (!data.result?.cid) {
                console.error('[API] getEmailCheck response:', data);
                throw new Error('No CID in response');
            }

            return data.result.cid;
        },
        1,
        1000,
        'getEmailCheck'
    );
}

/**
 * Email change for a quiz flow
 */
export async function getEmailChanged(
    apiEndpoint: string,
    flowEndpoint: string,
    cid: string,
    emailOld: string,
    emailNew: string,
    version: string
): Promise<string> {
    return fetchWithRetry(
        async () => {
            const requestBody: QuizEmailChangedRequest = {
                cid,
                emailOld,
                emailNew,
                version
            };

            const response = await fetch(`${apiEndpoint}/quiz/${flowEndpoint}/email-changed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                console.error(`[API] getEmailChanged error! status: ${response.status}`);
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: QuizEmailChangedResponse = await response.json();

            if (!data.result?.cid) {
                console.error('[API] getEmailChanged response:', data);
                throw new Error('No CID in response');
            }

            return data.result.cid;
        },
        1,
        1000,
        'getEmailChanged'
    );
}

/**
 * OneSignal: email change for old flow (only triger)
 */
export async function getEmailChangedOldFlow(
    apiEndpoint: string,
    cid: string,
    emailOld: string,
    emailNew: string,
    pageUrl: string
): Promise<void> {
    return fetchWithRetry(
        async () => {
            const requestBody: OldFlowEmailChangedRequest = {
                cid,
                emailOld,
                emailNew,
                version: 'quiz_old_email_change',
                page_url: pageUrl,
            };

            const response = await fetch(`${apiEndpoint}/quiz/email-changed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                console.error(`[API] getEmailChanged error! status: ${response.status}`);
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        },
        1,
        1000,
        'getEmailChangedOldFlow'
    );
}

/**
 * Binds email via social authentication
 */
export async function postUserLinkEmail(
    apiEndpoint: string,
    orderId: string,
    provider: string,
    email: string,
    pageUrl: string
): Promise<boolean> {
    return fetchWithRetry(
        async () => {
            const requestBody = {
                order_id: orderId,
                provider: provider.toUpperCase(),
                email: email,
                page_url: pageUrl,
            };

            const response = await fetch(`${apiEndpoint}/order/link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                return true;
            } else if (response.status === 400) {
                console.warn('[API] Email already linked for this provider');
                return true;
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        },
        1,
        1000,
        'postUserLinkEmail'
    );
}

/**
 * Checked user email link status
 */
export async function checkUserLinkedEmail(
    apiEndpoint: string,
    orderId: string
): Promise<boolean> {
    return fetchWithRetry(
        async () => {

            const response = await fetch(`${apiEndpoint}/order/link/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_id: orderId }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: EmailLinkStatusResponse = await response.json();

            return data.linked;
        },
        5,
        1000,
        'checkUserLinkedEmail'
    );
}

/**
 * Google Analytics Client ID
 */
export function getGoogleClientID(): Promise<string> {
    return new Promise((resolve, reject) => {
        try {
            if (typeof window === 'undefined' || !window.gtag) {
                reject(new Error('Google Analytics not available'));
                return;
            }

            window.gtag("get", "G-QWT83PZ7FZ", "client_id", (clientId: string) => {
                resolve(clientId);
            });
        } catch (error) {
            console.error('[API] Error getting Google client ID:', error);
            reject(error);
        }
    });
}
